import { z } from 'zod';

/** Synthetic + live product windows (plan §0.2 · WIN). No 5Y. */
export const WINDOW_KEYS = ['1M', '3M', '1Y', '3Y'] as const;
export const WindowKeySchema = z.enum(WINDOW_KEYS);
export type WindowKey = z.infer<typeof WindowKeySchema>;

/**
 * Index membership codes on a security row and in PG enums.
 * Product uses NDX. The demo emits the vendor's own spelling, which must never
 * reach a tuple, schema, DB label or URL param (plan §0.3 · traps), enforced by
 * `scripts/vocabulary-gate.mjs`. The forbidden spellings are named here once so
 * the rule is greppable from the canonical tuple:
 * IXIC / NDQ → NDX. vocabulary-gate-allow (sole sanctioned provenance mention).
 */
export const INDEX_CODES = ['SPX', 'NDX', 'DJI'] as const;
export const IndexCodeSchema = z.enum(INDEX_CODES);
export type IndexCode = z.infer<typeof IndexCodeSchema>;

/**
 * Universe filter keys for URL `idx` / API `index`.
 * `ALL` is a filter, not a membership code — keep it off INDEX_CODES.
 */
export const FILTER_KEYS = ['ALL', ...INDEX_CODES] as const;
export const FilterKeySchema = z.enum(FILTER_KEYS);
export type FilterKey = z.infer<typeof FilterKeySchema>;

/** SPA dashboard grouping; deliberately broader than the v1 areas API. */
export const TAXONOMY_KEYS = ['sector', 'industry'] as const;
export const DEFAULT_TAXONOMY = 'sector' as const;
export const TaxonomySchema = z.enum(TAXONOMY_KEYS);
export type Taxonomy = z.infer<typeof TaxonomySchema>;

/** `/v1/areas` grouping. v1 exposes industry and no overlapping layer. */
export const AREA_TAXONOMY_KEYS = ['industry'] as const;
export const DEFAULT_AREA_TAXONOMY = 'industry' as const;
export const AreaTaxonomySchema = z.enum(AREA_TAXONOMY_KEYS);
export type AreaTaxonomy = z.infer<typeof AreaTaxonomySchema>;

/** Table sort keys (demo colDefs + plan §6.6 sort-key contract). */
export const SORT_KEYS = [
  'ticker',
  'name',
  'sector',
  'ret',
  'xret',
  'vol',
  'beta',
  'dd',
  'risk',
  'mcap',
] as const;
export const SortKeySchema = z.enum(SORT_KEYS);
export type SortKey = z.infer<typeof SortKeySchema>;

export const SORT_DIRS = [1, -1] as const;
/* Derived from the tuple, not restated: the closed set must be a runtime
   iterable, a type and the Zod source at once (style §5.4 · naming §3.9). */
export const SortDirSchema = z.literal(SORT_DIRS);
export type SortDir = z.infer<typeof SortDirSchema>;

export const API_SORT_DIRS = ['asc', 'desc'] as const;
export const ApiSortDirSchema = z.enum(API_SORT_DIRS);
export type ApiSortDir = z.infer<typeof ApiSortDirSchema>;

/** 11 GICS sector codes from demo SECN / RAW (plan §0.2). */
export const SECTOR_IDS = [
  'TEC',
  'COM',
  'DIS',
  'STA',
  'FIN',
  'HLT',
  'IND',
  'ENE',
  'UTL',
  'REI',
  'MAT',
] as const;
export const SectorIdSchema = z.enum(SECTOR_IDS);
export type SectorId = z.infer<typeof SectorIdSchema>;

/**
 * `/v1/meta.dataSource` — a SAFETY classification, not the vendor name
 * (plan §6.6). `synthetic` covers local/synthetic adapters and seeded synthetic
 * HTTP tests; `vendor` covers production HTTP backed by non-synthetic OK ingest
 * runs. Production must never return `synthetic`; raw provider names stay in
 * provenance fields so this flag cannot grow a third meaning.
 */
export const DATA_SOURCES = ['synthetic', 'vendor'] as const;
export const DataSourceSchema = z.enum(DATA_SOURCES);
export type DataSource = z.infer<typeof DataSourceSchema>;

/** Analyst verdict labels (F15). SELL is not a verdict — REDUCE is. */
export const VERDICTS = ['BUY', 'HOLD', 'REDUCE'] as const;
export const VerdictSchema = z.enum(VERDICTS);
export type Verdict = z.infer<typeof VerdictSchema>;

export const CATALYST_TAGS = ['EARNINGS', 'ANALYST', 'SECTOR', 'CAPITAL'] as const;
export const CatalystTagSchema = z.enum(CATALYST_TAGS);
export type CatalystTag = z.infer<typeof CatalystTagSchema>;

export const DIVERSIFICATION_KINDS = ['inverse', 'uncorrelated', 'similar'] as const;
export const DiversificationKindSchema = z.enum(DIVERSIFICATION_KINDS);
export type DiversificationKind = z.infer<typeof DiversificationKindSchema>;

/**
 * Wire error codes (plan §6.6 + error-handling standards §1.2).
 * `SECURITY_NOT_FOUND` is not a code — unknown tickers use `NOT_FOUND`.
 */
export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'NOT_FOUND',
  'SECURITY_DELISTED',
  'UNAUTHENTICATED',
  'RATE_LIMITED',
  'PAYLOAD_TOO_LARGE',
  'NO_PUBLISHED_SNAPSHOT',
  'INTERNAL',
  'LAST_CREDENTIAL',
] as const;
export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;
